/**
 * Event Polling Service
 * Polls the events endpoint for real-time event delivery
 * Since API Gateway HTTP API doesn't support SSE streaming from Lambda,
 * we poll the JSON endpoint instead
 */
import { buildApiError } from './api-error.service';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
class SSEService {
    constructor() {
        Object.defineProperty(this, "pollIntervalId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "abortController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "retryCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "pollCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "isConnecting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isConnected", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    /**
     * Connect to events endpoint using polling
     */
    connect(options) {
        if (this.isConnecting || this.isConnected) {
            console.warn('Event polling already established or connecting');
            return;
        }
        // Disconnect any existing connection first
        this.disconnect();
        this.isConnecting = true;
        this.retryCount = 0;
        this.pollCount = 0;
        this.currentOptions = options;
        // Mark as connected immediately (polling starts)
        this.isConnecting = false;
        this.isConnected = true;
        options.onOpen?.();
        console.info('Event polling started');
        // Start polling
        this._startPolling(options);
    }
    /**
     * Start polling the events endpoint
     */
    _startPolling(options) {
        const { correlationId, authToken, onMessage, onError, onClose, onTimeout, pollInterval = 2000, // Poll every 2 seconds
        maxPollAttempts = 25, // Max 50 seconds (25 * 2s) for cold-start tolerance
         } = options;
        // Cancel previous polling if exists
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
        }
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        // Build URL with query params
        const url = new URL(`${API_BASE_URL}/api/audit/events`);
        if (correlationId) {
            url.searchParams.set('correlationId', correlationId);
        }
        const poll = async () => {
            // Check if we've exceeded max poll attempts
            if (this.pollCount >= maxPollAttempts) {
                console.warn('Max poll attempts reached, stopping polling - animal lookup timed out');
                this.disconnect();
                onTimeout?.(); // Call timeout handler
                onClose?.();
                return;
            }
            // Check if aborted
            if (this.abortController?.signal.aborted) {
                return;
            }
            this.pollCount++;
            try {
                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        Accept: 'application/json',
                    },
                    signal: this.abortController?.signal,
                });
                if (!response.ok) {
                    throw await buildApiError(response);
                }
                const message = await response.json();
                // Handle the response
                if (message.type === 'rapid-audit-animal-lookup-completed') {
                    // Event found, stop polling and notify
                    console.info('Event received, stopping polling');
                    onMessage?.(message);
                    this.disconnect();
                    onClose?.();
                }
                else if (message.type === 'pending') {
                    // Still waiting, continue polling
                    console.debug(`Event pending (poll ${this.pollCount}/${maxPollAttempts})`);
                }
                else if (message.type === 'connected') {
                    // Initial connection confirmation
                    console.info('Event polling connection confirmed');
                    onMessage?.(message);
                }
                else {
                    // Unknown message type, but forward it anyway
                    onMessage?.(message);
                }
            }
            catch (error) {
                // Don't log abort errors (intentional disconnects)
                if (error instanceof Error && error.name !== 'AbortError') {
                    console.error('Event polling error', error);
                    // Create a synthetic error event for the error handler
                    const errorEvent = new ErrorEvent('error', {
                        message: error.message,
                        error,
                    });
                    onError?.(errorEvent);
                    // Retry logic for connection errors
                    const maxRetries = options.maxRetries ?? 5;
                    const retryDelay = options.retryDelay ?? 3000;
                    if (this.retryCount < maxRetries) {
                        this.retryCount++;
                        console.info(`Retrying event polling (attempt ${this.retryCount}/${maxRetries})...`);
                        // Wait before retrying
                        setTimeout(() => {
                            if (!this.abortController?.signal.aborted) {
                                poll();
                            }
                        }, retryDelay * this.retryCount); // Exponential backoff
                    }
                    else {
                        console.error('Max retry attempts reached');
                        this.disconnect();
                        onClose?.();
                    }
                }
            }
        };
        // Start polling immediately, then at intervals
        poll();
        this.pollIntervalId = setInterval(poll, pollInterval);
    }
    /**
     * Disconnect from event polling
     */
    disconnect() {
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
        }
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.isConnected = false;
        this.isConnecting = false;
        this.retryCount = 0;
        this.pollCount = 0;
        // Don't clear currentOptions - keep it for reconnect
    }
    /**
     * Reconnect with current options
     */
    reconnect() {
        if (this.currentOptions) {
            // Reset state but keep options
            this.isConnected = false;
            this.isConnecting = false;
            this.retryCount = 0;
            this.pollCount = 0;
            if (this.pollIntervalId) {
                clearInterval(this.pollIntervalId);
                this.pollIntervalId = null;
            }
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            // Connect with stored options
            this.connect(this.currentOptions);
        }
    }
    /**
     * Check if connected
     */
    get connected() {
        return this.isConnected;
    }
}
// Singleton instance
export const sseService = new SSEService();
//# sourceMappingURL=sse.service.js.map